import React from "react";
import _ from "lodash";
import T from "prop-types";
import { Col, Row } from "react-bootstrap";
import moment from "moment";
import CancellationStatsTooltip from "MODULES/shared/components/StatsTooltip/CompanyStats/CancellationStatsTooltip";
import DetailStat from "../DetailStat";
import {
  dollar,
  percent,
  number,
  numberAndPercent,
  dollarAndPercent
} from "../format";

const containerStyles = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  paddingTop: 20,
  paddingBottom: 20
};

const statsContainerStyles = {
  width: "100%"
};

const headerStyles = {
  alignSelf: "flex-start",
  margin: 0,
  paddingLeft: 20,
  paddingRight: 20,
  color: "gray",
  minWidth: 90,
  textTransform: "none"
};

const separatorStyles = {
  borderTop: "1px solid rgba(0, 0, 0, 0.15)",
  marginTop: 10,
  marginBottom: 20
};

export default function Detail({ stats, context }) {
  const showRepairStats =
    context === "repair" ||
    _.some(stats.repairs, value => Math.abs(value) !== 0);
  const showSalesStats =
    context === "standard" ||
    _.some(stats.sales, value => Math.abs(value) !== 0);
  const showPurchasingStats = _.some(
    stats.purchases,
    value => Math.abs(value) !== 0
  );

  let defaultStats;
  if (context === "repair") {
    defaultStats = <CompanyRepairStatsDetail stats={stats.repairs || {}} />;
  } else {
    defaultStats = <CompanySaleStatsDetail stats={stats.sales || {}} />;
  }

  let expandedStats;
  if (context === "repair") {
    expandedStats = (
      <React.Fragment>
        {showPurchasingStats && (
          <React.Fragment>
            <hr style={separatorStyles} />
            <CompanyPurchaseStatsDetail stats={stats.purchases} />
          </React.Fragment>
        )}
        {showSalesStats && (
          <React.Fragment>
            <hr style={separatorStyles} />
            <CompanySaleStatsDetail stats={stats.sales} />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  } else {
    expandedStats = (
      <React.Fragment>
        {showPurchasingStats && (
          <React.Fragment>
            <hr style={separatorStyles} />
            <CompanyPurchaseStatsDetail stats={stats.purchases} />
          </React.Fragment>
        )}
        {showRepairStats && (
          <React.Fragment>
            <hr style={separatorStyles} />
            <CompanyRepairStatsDetail stats={stats.repairs} />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {defaultStats}
      {expandedStats}
    </React.Fragment>
  );
}

Detail.propTypes = {
  stats: T.shape({}),
  company: T.shape({
    id: T.number.isRequired,
    name: T.string.isRequired
  }),
  context: T.oneOf(["standard", "repair"])
};

export function CompanyRepairStatsDetail({ stats }) {
  const year = moment().year();

  return (
    <div style={containerStyles}>
      <h4 style={headerStyles}>Repairs</h4>
      <div style={statsContainerStyles}>
        <DetailStat
          label={`# Repairs Sent - ${year}`}
          value={number(stats.repairs_current_year_count)}
          offset={2}
        />
        <DetailStat
          label={`$ Repairs Sent - ${year}`}
          value={dollar(stats.repairs_current_year_dollars)}
        />
        <DetailStat
          label={`# Repairs Sent - ${year - 1}`}
          value={number(stats.repairs_last_year_count)}
        />
        <DetailStat
          label={`$ Repairs Sent - ${year - 1}`}
          value={dollar(stats.repairs_last_year_dollars)}
        />
        <Row>
          <DetailStat
            label="# Total Warranties - 12 mos."
            value={numberAndPercent(
              stats.warranties_12_months_count,
              stats.warranties_12_months_percent
            )}
            offset={3}
          />
          <DetailStat
            label="# Warranties Accepted - 12 mos."
            value={numberAndPercent(
              stats.warranties_approved_12_months_count,
              stats.warranties_approved_12_months_percent
            )}
          />
          <DetailStat
            label="# Warranties Denied - 12 mos."
            value={numberAndPercent(
              stats.warranties_denied_12_months_count,
              stats.warranties_denied_12_months_percent
            )}
          />
        </Row>
        <Row>
          <DetailStat
            label="Avg # Days to Eval (Mean)"
            value={number(stats.mean_days_to_eval)}
            offset={3}
            smOffset={3}
          />
          <DetailStat
            label="Avg # Days to Eval (Median)"
            value={number(stats.median_days_to_eval)}
          />
          <DetailStat
            label="Avg # Days to Eval (Mode)"
            value={number(stats.mode_days_to_eval)}
          />
        </Row>
        <DetailStat
          label="# Units in Stock Tagged By"
          value={number(stats.units_in_stock_tagged_by_qty)}
          offset={4}
          smOffset={4}
        />
        <DetailStat
          label="# Units At Shop"
          value={number(stats.units_at_shop_qty)}
        />
      </div>
    </div>
  );
}

CompanyRepairStatsDetail.propTypes = {
  stats: T.shape({})
};

export function CompanyPurchaseStatsDetail({ stats }) {
  const year = moment().year();

  return (
    <div style={containerStyles}>
      <h4 style={headerStyles}>Purchasing</h4>
      <div style={statsContainerStyles}>
        <DetailStat
          label="IQs - 6 mos."
          value={number(stats.iqs_6_months_count)}
          offset={1}
        />
        <DetailStat
          label="Purch. by Us - 6 mos."
          value={number(stats.purchases_6_months_count)}
        />
        <DetailStat
          label="IQ/PO Conv. % - 6 mos."
          value={percent(stats.po_to_iq_6_months_percent)}
        />
        <DetailStat
          label="# PO Line Items - 6 mos."
          value={number(stats.purchase_items_6_months_count)}
        />
        <DetailStat
          label={[
            "# PO Line Items CXL - 6 mos.",
            <CancellationStatsTooltip stats={stats} />
          ]}
          value={numberAndPercent(
            stats.purchase_items_cancelled_6_months_count,
            stats.purchase_items_cancelled_6_months_percent
          )}
        />
        <Col md={1} sm={1}>
          &nbsp;
        </Col>
        <DetailStat
          label={`Purch. By Us - ${year}`}
          value={dollar(stats.purchases_current_year_dollars)}
          offset={2}
        />
        <DetailStat
          label={`$ CXL on Us - ${year}`}
          value={dollarAndPercent(
            stats.purchases_cancelled_current_year_dollars,
            stats.purchases_cancelled_current_year_dollars_percent
          )}
        />
        <DetailStat
          label={`Purch. By Us - ${year - 1}`}
          value={dollar(stats.purchases_last_year_dollars)}
        />
        <DetailStat
          label={`$ CXL on Us - ${year - 1}`}
          value={dollarAndPercent(
            stats.purchases_cancelled_last_year_dollars,
            stats.purchases_cancelled_last_year_dollars_percent
          )}
        />
      </div>
    </div>
  );
}

CompanyPurchaseStatsDetail.propTypes = {
  stats: T.shape({})
};

export function CompanySaleStatsDetail({ stats }) {
  const year = moment().year();

  return (
    <div style={containerStyles}>
      <h4 style={headerStyles}>Sales</h4>
      <div style={statsContainerStyles}>
        <DetailStat
          label="OQs - 6 mos."
          value={number(stats.oqs_6_months_count)}
          offset={1}
        />
        <DetailStat
          label="Sold by Us - 6 mos."
          value={number(stats.sales_6_months_count)}
        />
        <DetailStat
          label="OQ/SO Conv. % - 6 mos."
          value={percent(stats.so_to_oq_6_months_percent)}
        />
        <DetailStat
          label="# SO Line Items - 6 mos."
          value={number(stats.sales_items_6_months_count)}
        />
        <DetailStat
          label="# SO Line Items CXL - 6 mos."
          value={numberAndPercent(
            stats.sales_items_cancelled_6_months_count,
            stats.sales_items_cancelled_6_months_percent
          )}
        />
        <Col md={1} sm={1}>
          &nbsp;
        </Col>
        <DetailStat
          label={`Sold By Us - ${year}`}
          value={dollar(stats.sales_current_year_dollars)}
          offset={4}
        />
        <DetailStat
          label={`Sold By Us - ${year - 1}`}
          value={dollar(stats.sales_last_year_dollars)}
        />
        <Col md={4} sm={4}>
          &nbsp;
        </Col>
      </div>
    </div>
  );
}

CompanySaleStatsDetail.propTypes = {
  stats: T.shape({})
};



// WEBPACK FOOTER //
// ./app/modules/shared/components/StatsTooltip/CompanyStats/Detail.jsx